<template>
  <div v-if="isOpen" class="modal-overlay" @click="handleClose">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2>Éditer l'annotation</h2>
        <button @click="handleClose" class="btn-close">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      <div class="modal-body">
        <div class="form-group">
          <label for="annotation-name">Nom</label>
          <input
            id="annotation-name"
            v-model="formData.name"
            type="text"
            class="form-input"
            :placeholder="getDefaultName()"
          />
        </div>

        <div class="form-group">
          <label for="annotation-description">Description</label>
          <textarea
            id="annotation-description"
            v-model="formData.description"
            class="form-textarea"
            rows="3"
            placeholder="Ajouter une description..."
          ></textarea>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="annotation-color">Couleur</label>
            <div class="color-input-wrapper">
              <input
                id="annotation-color"
                v-model="formData.color"
                type="color"
                class="form-color"
              />
              <input
                v-model="formData.color"
                type="text"
                class="form-input color-text"
                placeholder="#000000"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="annotation-stroke">Épaisseur</label>
            <div class="stroke-input-wrapper">
              <input
                id="annotation-stroke"
                v-model.number="formData.strokeWidth"
                type="range"
                min="5"
                max="50"
                class="form-range"
              />
              <span class="stroke-value">{{ Math.round(formData.strokeWidth) }} px</span>
            </div>
          </div>
        </div>

        <div class="preview-section">
          <label>Aperçu</label>
          <svg class="preview-svg" viewBox="0 0 200 100">
            <line
              v-if="annotation?.type === 'line' || annotation?.type === 'measure'"
              x1="20"
              y1="50"
              x2="180"
              y2="50"
              :stroke="formData.color || getDefaultColor()"
              :stroke-width="previewStrokeWidth"
              stroke-linecap="round"
            />
            <rect
              v-else-if="annotation?.type === 'rectangle'"
              x="40"
              y="20"
              width="120"
              height="60"
              :stroke="formData.color || getDefaultColor()"
              :stroke-width="previewStrokeWidth"
              fill="none"
            />
            <circle
              v-else-if="annotation?.type === 'circle'"
              cx="100"
              cy="50"
              r="30"
              :stroke="formData.color || getDefaultColor()"
              :stroke-width="previewStrokeWidth"
              fill="none"
            />
          </svg>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="handleClose" class="btn btn-secondary">Annuler</button>
        <button @click="handleSave" class="btn btn-primary" :disabled="saving">
          {{ saving ? 'Enregistrement...' : 'Enregistrer' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import type { Annotation } from '@api/annotations.api';
import { ANNOTATION_DEFAULT_STROKE_PX } from '@/constants/annotations';

const props = defineProps<{
  isOpen: boolean;
  annotation: Annotation | null;
}>();

const emit = defineEmits<{
  close: [];
  save: [data: { name?: string | null; description?: string | null; color?: string | null; strokeWidth?: number }];
}>();

const saving = ref(false);

const defaultStroke = ANNOTATION_DEFAULT_STROKE_PX;

function clampStrokeWidth(v: number): number {
  return Math.min(50, Math.max(5, Math.round(v)));
}

const formData = ref({
  name: '',
  description: '',
  color: '',
  strokeWidth: defaultStroke,
});

/** Aperçu miniature (SVG 200×100) */
const previewStrokeWidth = computed(() => {
  const w = formData.value.strokeWidth;
  return Math.min(8, Math.max(1, w / 8));
});

const getDefaultName = (): string => {
  if (!props.annotation) return '';
  const labels: Record<string, string> = {
    label: 'Étiquette',
    line: 'Ligne',
    rectangle: 'Rectangle',
    circle: 'Cercle',
    measure: 'Mesure',
  };
  return labels[props.annotation.type] || props.annotation.type;
};

const getDefaultColor = (): string => {
  if (!props.annotation) return '#4a9eff';
  const colors: Record<string, string> = {
    label: '#ffff00',
    line: '#ff0000',
    rectangle: '#ffa500',
    circle: '#00ff00',
    measure: '#ff00ff',
  };
  return colors[props.annotation.type] || '#4a9eff';
};

watch(
  () => props.annotation,
  (newAnnotation) => {
    if (newAnnotation) {
      formData.value = {
        name: newAnnotation.name || '',
        description: newAnnotation.description || '',
        color: newAnnotation.color || getDefaultColor(),
        strokeWidth: clampStrokeWidth(
          newAnnotation.stroke_width != null ? Number(newAnnotation.stroke_width) : defaultStroke
        ),
      };
    }
  },
  { immediate: true }
);

const handleClose = () => {
  emit('close');
};

const handleSave = () => {
  const data = {
    name: formData.value.name || null,
    description: formData.value.description || null,
    color: formData.value.color || null,
    strokeWidth: clampStrokeWidth(formData.value.strokeWidth),
  };
  emit('save', data);
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: #2a2a2a;
  border: 1px solid #444;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #444;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #e0e0e0;
}

.btn-close {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-close:hover {
  background-color: #444;
  color: #e0e0e0;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.9rem;
  font-weight: 500;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 0.625rem;
  background-color: #1a1a1a;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  font-size: 0.9rem;
  font-family: inherit;
  transition: border-color 0.2s;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #4a9eff;
}

.form-textarea {
  resize: vertical;
  min-height: 60px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.color-input-wrapper {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.form-color {
  width: 50px;
  height: 38px;
  border: 1px solid #444;
  border-radius: 4px;
  background-color: #1a1a1a;
  cursor: pointer;
}

.color-text {
  flex: 1;
}

.stroke-input-wrapper {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.form-range {
  flex: 1;
  height: 6px;
  background: #444;
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;
}

.form-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: #4a9eff;
  border-radius: 50%;
  cursor: pointer;
}

.form-range::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: #4a9eff;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}

.stroke-value {
  min-width: 40px;
  text-align: right;
  color: #e0e0e0;
  font-size: 0.85rem;
}

.preview-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #444;
}

.preview-section label {
  display: block;
  margin-bottom: 0.75rem;
  color: #e0e0e0;
  font-size: 0.9rem;
  font-weight: 500;
}

.preview-svg {
  width: 100%;
  height: 100px;
  background-color: #1a1a1a;
  border: 1px solid #444;
  border-radius: 4px;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #444;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn {
  padding: 0.625rem 1.25rem;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background-color: #333;
  color: #e0e0e0;
}

.btn-secondary:hover {
  background-color: #444;
}

.btn-primary {
  background-color: #4a9eff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #3a8eef;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
